import React, { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { useTheme } from "../context/ThemeContext";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AuditLogModal from "./AuditLogModal";
import { Plus, Trash2, Edit3, Calendar } from "lucide-react";
import { formatRODate } from "../utils/formatDate";
import DatePicker from "react-datepicker";

const AuditLogPage = () => {
    const { darkMode } = useTheme();

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterEntity, setFilterEntity] = useState("ALL");
    const [filterDate, setFilterDate] = useState(new Date());

    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const DEV_MODE = true;

    const userList = [...new Set(logs.map(l => l.userName))];
    const [filterUser, setFilterUser] = useState("ALL");


    const [sortConfig, setSortConfig] = useState({
        key: "createdAt",
        direction: "desc",
    });

    const formatMonthYearRO = (monthYear) => {
        if (!monthYear) return null;

        const [year, month] = monthYear.split("-");
        const date = new Date(Number(year), Number(month) - 1, 1);

        return date.toLocaleDateString("ro-RO", {
            month: "long",
            year: "numeric",
        });
    };

    const entityNameMap = {
        Customer: "Client",
        User: "Utilizator",
        Order: "Comandă",
        Product: "Produs",
        Offer: "Ofertă",
        AdvancePayment: "Avans",
        CommissionApproval: "Aprobare comision",
    };

    const buildSummary = (log) => {
        const user = `<b>${log.userName}</b>`;
        const action = log.actionType.toLowerCase();

        const act = action.includes("create") ? "a creat"
            : action.includes("update") ? "a modificat"
                : action.includes("delete") ? "a șters"
                    : "a efectuat o acțiune";

        switch (log.entityName) {
            case "Order":
                return `${user} ${act} <b>comanda</b> cu numărul <b>${log.entityDisplayName}</b>`;
            case "Customer":
                return `${user} ${act} <b>clientul</b> „<b>${log.entityDisplayName}</b>”`;
            case "Product":
                return `${user} ${act} <b>produsul</b> „<b>${log.entityDisplayName}</b>”`;
            case "User":
                return `${user} ${act} <b>utilizatorul</b> „<b>${log.entityDisplayName}</b>”`;
            case "Offer":
                return `${user} ${act} <b>oferta</b> cu numărul <b>${log.entityDisplayName}</b>`;
            case "AdvancePayment":
                return `${user} ${act} <b>un avans</b> pentru comanda <b>${log.entityDisplayName}</b>`;
            case "CommissionApproval": {
                let monthYear = null;

                if (log.newValues) {
                    try {
                        const parsed = JSON.parse(log.newValues);
                        monthYear = parsed.MonthYear;
                    } catch {
                        monthYear = null;
                    }
                }

                const periodText = monthYear
                    ? ` pentru luna <b>${formatMonthYearRO(monthYear)}</b>`
                    : "";

                return `${user} ${act} <b>aprobarea comisionului</b>${periodText}`;
            }

            default:
                return `${user} ${act} <b>${log.entityName.toLowerCase()}</b>`;
        }
    };


    const getActionBadge = (type) => {
        const map = {
            create: {
                text: "Creat",
                border: "border-green-600",
                color: "text-green-600",
                icon: <Plus size={16} className="text-green-600" />,
            },
            update: {
                text: "Modificat",
                border: "border-yellow-600",
                color: "text-yellow-600",
                icon: <Edit3 size={16} className="text-yellow-600" />,
            },
            delete: {
                text: "Șters",
                border: "border-red-600",
                color: "text-red-600",
                icon: <Trash2 size={16} className="text-red-600" />,
            },
        };

        const normalized =
            type.toLowerCase().includes("create") ? "create" :
                type.toLowerCase().includes("update") ? "update" :
                    type.toLowerCase().includes("delete") ? "delete" : "update";

        const cfg = map[normalized];

        return (
            <span
                className={`
                inline-flex items-center justify-center gap-2
                px-3 py-1 rounded-full text-sm font-semibold
                border ${cfg.border} ${cfg.color}
                w-[120px]
                bg-transparent
            `}
            >
                {cfg.icon}
                {cfg.text}
            </span>
        );
    };

    useEffect(() => {
        const load = async () => {
            const token = localStorage.getItem("token");
            const res = await fetch(
                `${API_BASE_URL}/api/audit-logs?page=1&pageSize=300`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = await res.json();
            setLogs(data.data || []);
            setLoading(false);
        };
        load();
    }, []);

    const filtered = logs.filter((l) => {
        if (filterEntity !== "ALL" && l.entityName !== filterEntity) return false;
        if (filterUser !== "ALL" && l.userName !== filterUser) return false;

        if (filterDate) {
            const logDate = new Date(l.createdAt);
            const selected = filterDate;

            const same =
                logDate.getFullYear() === selected.getFullYear() &&
                logDate.getMonth() === selected.getMonth() &&
                logDate.getDate() === selected.getDate();

            if (!same) return false;
        }

        const t = searchTerm.toLowerCase();

        return (
            l.userName?.toLowerCase().includes(t) ||
            l.entityDisplayName?.toLowerCase().includes(t) ||
            l.actionType?.toLowerCase().includes(t)
        );
    });

    const sorted = [...filtered].sort((a, b) => {
        const field = sortConfig.key;
        let A = a[field];
        let B = b[field];

        if (field === "createdAt") {
            A = new Date(A);
            B = new Date(B);
        }

        if (A < B) return sortConfig.direction === "asc" ? -1 : 1;
        if (A > B) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction:
                prev.key === key
                    ? prev.direction === "asc"
                        ? "desc"
                        : "asc"
                    : "asc",
        }));
    };

    const SortIcon = ({ column }) => (
        <div className="flex flex-col items-center leading-none ml-1">
            <ArrowUpwardIcon
                fontSize="inherit"
                className={
                    sortConfig.key === column && sortConfig.direction === "asc"
                        ? ""
                        : "opacity-30"
                }
            />
            <ArrowDownwardIcon
                fontSize="inherit"
                className={
                    sortConfig.key === column && sortConfig.direction === "desc"
                        ? ""
                        : "opacity-30"
                }
            />
        </div>
    );

    // if (DEV_MODE) {
    //     return (
    //         <div className={`min-h-screen flex flex-col items-center justify-center 
    //         ${darkMode ? "bg-gray-900 text-white" : "bg-gray-200 text-gray-900"}`}>

    //             <div className="flex flex-col items-center gap-4">
    //                 <span className="text-yellow-500 text-6xl">🚧</span>

    //                 <h1 className="text-3xl font-bold">Modul în curs de dezvoltare</h1>



    //                 <div className={`mt-6 px-6 py-2 rounded-full font-semibold 
    //                 ${darkMode ? "bg-gray-800 border border-gray-700" : "bg-white border border-gray-300"}`}>
    //                     Acces temporar dezactivat
    //                 </div>
    //             </div>
    //         </div>
    //     );
    // }

    if (loading)
        return (
            <p className="text-center text-xl mt-10">
                Se încarcă...
            </p>
        );

    return (

        <div className={`min-h-screen p-6 ${darkMode ? "bg-gray-900" : "bg-gray-200"}`}>

            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-semibold ${darkMode ? "text-white" : "text-black"}`}>
                    Istoric acțiuni
                </h2>

                <div className="relative flex items-center gap-8">
                    <SearchIcon
                        className={`absolute left-3 ${darkMode ? "text-gray-300" : "text-gray-500"
                            }`}
                    />
                    <input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Căutare..."
                        className={`pl-10 pr-4 py-2 rounded-3xl w-80 border shadow-sm ${darkMode
                            ? "bg-gray-800 text-white border-gray-700"
                            : "bg-white text-black border-gray-300"
                            }`}
                    />

                    <div className="flex items-center gap-3">
                        <label className="font-semibold text-sm">Dată:</label>

                        <DatePicker
                            selected={filterDate}
                            onChange={(d) => setFilterDate(d)}
                            dateFormat="dd.MM.yyyy"
                            calendarStartDay={1}
                            portalId="datepicker-root"       // ⬅️ EZ HIÁNYZOTT!
                            customInput={
                                <div
                                    className={`flex items-center justify-between w-[160px] h-[44px] px-4 
                    rounded-xl shadow-sm border-2 transition-all cursor-pointer
                    ${darkMode
                                            ? "bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700"
                                            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    <span className="font-semibold text-sm">
                                        {filterDate
                                            ? filterDate.toLocaleDateString("ro-RO")
                                            : "Alege data"}
                                    </span>

                                    <Calendar
                                        className={`w-5 h-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
                                    />
                                </div>
                            }
                        />
                    </div>

                    <select
                        value={filterUser}
                        onChange={(e) => setFilterUser(e.target.value)}
                        className={`p-2 rounded-3xl border ${darkMode
                            ? "bg-gray-800 text-white border-gray-600"
                            : "bg-white text-black border-gray-300"
                            }`}
                    >
                        <option value="ALL">Toți utilizatorii</option>
                        {userList.map((u) => (
                            <option key={u} value={u}>
                                {u}
                            </option>
                        ))}
                    </select>

                    <select
                        value={filterEntity}
                        onChange={(e) => setFilterEntity(e.target.value)}
                        className={`p-2 rounded-3xl border ${darkMode
                            ? "bg-gray-800 text-white border-gray-600"
                            : "bg-white text-black border-gray-300"
                            }`}
                    >
                        <option value="ALL">Toate</option>
                        {Object.keys(entityNameMap).map((key) => (
                            <option key={key} value={key}>
                                {entityNameMap[key]}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* TABLE */}
            <div className="overflow-x-auto">
                <table
                    className={`w-full border text-sm ${darkMode
                        ? "border-gray-700 bg-gray-900 text-gray-300"
                        : "border-gray-400 bg-white text-gray-800 shadow-lg"
                        }`}
                >
                    <thead>
                        <tr className={`${darkMode ? "bg-gray-700 text-white" : "bg-gray-300 text-gray-900"}`}>
                            <th
                                className="border p-2 min-w-[130px] text-center cursor-pointer"
                                onClick={() => handleSort("createdAt")}
                            >
                                Dată <SortIcon column="createdAt" />
                            </th>

                            <th className="border p-2 min-w-[150px] text-center">
                                Entitate
                            </th>

                            <th className="border p-2 min-w-[350px] text-center">
                                Acțiune
                            </th>

                            <th className="border p-2 min-w-[150px] text-center">
                                Tip
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {sorted.map((log, idx) => (
                            <tr
                                key={log.id}
                                onClick={() => {
                                    setSelectedLog(log);
                                    setIsModalOpen(true);
                                }}
                                className={`cursor-pointer transition ${darkMode
                                    ? idx % 2 === 0
                                        ? "bg-gray-800 hover:bg-gray-700"
                                        : "bg-gray-900 hover:bg-gray-700"
                                    : idx % 2 === 0
                                        ? "bg-gray-100 hover:bg-gray-200"
                                        : "bg-white hover:bg-gray-200"
                                    }`}
                            >
                                {/* DATĂ */}
                                <td className="border p-2 text-center">
                                    {formatRODate(log.createdAt)}
                                </td>

                                {/* ENTITATE */}
                                <td className="border p-2 text-center font-semibold">
                                    {entityNameMap[log.entityName] || log.entityName}
                                </td>

                                {/* ACȚIUNE (summary human) */}
                                <td
                                    className="border p-2 text-center text-left font-medium"
                                    dangerouslySetInnerHTML={{ __html: buildSummary(log) }}
                                ></td>

                                {/* TIP (badge) */}
                                <td className="border p-2 text-center">
                                    {getActionBadge(log.actionType)}
                                </td>
                            </tr>
                        ))}
                    </tbody>

                </table>
            </div>

            <AuditLogModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                log={selectedLog}
            />
        </div>
    );
};

export default AuditLogPage;
